import React, { useEffect, useRef, useState } from 'react';
import './InstagramFacebookStory.css';
import './styles.css';
import Card from 'react-bootstrap/Card';
import Image from 'react-bootstrap/Image';

import Carousel from 'react-bootstrap/Carousel';
import VideoComponent from './VideoComponent';
import { useTranslate } from '@tolgee/react';
import { PREVIEW_TYPE_COMPACT } from '../../config/constants';
import { useSelector } from 'react-redux';
import { COMPACT_MODE_HEIGHT } from '../contants';
import OnImagesLoaded from '../../../onImagesLoaded';
import PreviewImagesSkeleton from '../loaders/previewImagesSkeleton';
import PlatformIconSkeleton from '../loaders/platformIconSkeleton';
import ImportedPostsMediaPreview from './importedPostsMediaPreview';
import { Facebook } from 'react-content-loader';
import { FACEBOOK } from '../../../../config/constants';
import AltTagButton from './buttons/altTagsBtn';

export default function InstagramFacebookStory({
  platform = FACEBOOK,
  cropList = null,
  mediaFiles = null,
  postText = null,
  profileName = null,
  platformIcon = null,
  previewAssets,
  viewAssets
}) {
  const { t } = useTranslate();
  const [index, setIndex] = useState(0);
  const [activeIndicator, setActiveIndicator] = useState(0);
  const {
    ui: { previewMode },
    created_in_lomavis
  } = useSelector((state: any) => state.lomavisCreator);

  const videoRefs = useRef<Array<React.RefObject<HTMLVideoElement> | null>>([]);

  useEffect(() => {
    if (!mediaFiles || mediaFiles.length === 0) {
      videoRefs.current = [];
      return;
    }

    videoRefs.current = mediaFiles.map((media) =>
      media.type === 'video' ? React.createRef<HTMLVideoElement>() : null
    );
    setIndex(0);
    setActiveIndicator(0);
  }, [mediaFiles]);

  const pauseVideoAt = (mediaIndex: number) => {
    const video = videoRefs.current?.[mediaIndex]?.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  const playVideoAt = (mediaIndex: number) => {
    const video = videoRefs.current?.[mediaIndex]?.current;
    if (video) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  };

  const handleSlideChange = (selectedIndex: number) => {
    pauseVideoAt(index);
    setIndex(selectedIndex);
    setActiveIndicator(selectedIndex);
    playVideoAt(selectedIndex);
  };

  const EditButton = () => (
    <div
      style={{ zIndex: 7, top: '10px', right: '10px' }}
      className="d-flex gap-2 justify-content-center align-items-center position-absolute ms-auto me-3 mt-3 end-0"
    >
      <div
        className="viewButton btn btn-sm btn-bg-white border border-dark px-3"
        onClick={() => viewAssets(index)}
      >
        <i
          style={{ padding: '0px' }}
          className="fa-regular fa-eye text-dark"
        ></i>
      </div>

      <AltTagButton platform={platform} />

      <div
        className="btn btn-sm btn-bg-white border border-dark"
        onClick={() => previewAssets(index)}
      >
        <span className="loading-indicator spinner-border spinner-border-sm d-none"></span>
        <span>{t('lomavisCreator.preview.edit')}</span>
      </div>
    </div>
  );

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    if (mediaFiles && mediaFiles.length > 0) {
      const hasImage = mediaFiles.some((item) => item.type === 'image');
      if (!hasImage) {
        // When only videos are present, mark as loaded so Edit button appears.
        setIsImageLoaded(true);
      }
    }
  }, [mediaFiles]);

  const renderMedia = (media, idx) => {
    if (media?.type === 'video') {
      return (
        <VideoComponent
          useProcessedFrame={true}
          posterUrl={media?.custom_thumbnail_url}
          videoUrl={media?.video_url}
          autoplay={index === idx}
          backgroundUrl={media?.thumbnail_url}
          hideController={false}
          ref={videoRefs.current?.[idx]}
          styles={{
            maxHeight:
              previewMode == PREVIEW_TYPE_COMPACT
                ? COMPACT_MODE_HEIGHT
                : '100%',
            height: '100%',
            width: '100%'
          }}
        />
      );
    }

    return (
      <div
        className="w-100 rounded"
        style={{
          aspectRatio: 9 / 16,
          maxHeight:
            previewMode == PREVIEW_TYPE_COMPACT ? COMPACT_MODE_HEIGHT : '100%',
          backgroundImage: `url(${media?.thumbnail_url})`
        }}
      >
        <Image
          className="img-fluid w-100 h-100 rounded"
          style={{
            objectFit: 'contain',
            backdropFilter: 'blur(5px)',
            maxHeight:
              previewMode == PREVIEW_TYPE_COMPACT ? COMPACT_MODE_HEIGHT : '100%'
          }}
          src={media?.image_url}
        />
      </div>
    );
  };

  return (
    <div className="w-100 m-2">
      <Card className="d-flex overlay-wrapper justify-content-center w-100 h-100 bg-black position-relative rounded">
        <div
          className="d-flex w-100 flex-column justify-content-between p-2 px-3 position-absolute top-0"
          style={{ zIndex: 3 }}
        >
          <div className="d-flex flex-row align-items-center py-2">
            <div className="w-40px h-40px">
              <OnImagesLoaded
                onLoaded={() => {}}
                placeholder={<PlatformIconSkeleton />}
              >
                <Image
                  src={platformIcon}
                  className="img-fluid w-100 h-100"
                  roundedCircle
                />
              </OnImagesLoaded>
            </div>
            <div className="d-flex flex-column align-items-start ms-2">
              <span className="fw-bold" style={{ color: 'lightgray' }}>
                {profileName}
              </span>
            </div>
          </div>
        </div>
        {isImageLoaded && created_in_lomavis && <EditButton />}
        <div
          className="carousel-indicators"
          style={{
            display: 'flex'
          }}
        >
          {mediaFiles && (
            <>
              {mediaFiles.map((media, id) => (
                <button
                  key={id}
                  type="button"
                  data-bs-target=""
                  aria-label={'Slide ' + id}
                  aria-current="false"
                  className={activeIndicator == id ? 'active' : ''}
                  style={{
                    zIndex: 6
                  }}
                ></button>
              ))}
            </>
          )}
        </div>

        {mediaFiles &&
          created_in_lomavis &&
          mediaFiles.filter(
            (item) => item.type === 'image' || item.type === 'video'
          ).length > 0 && (
            <div
              className="asset_wrapper w-100 h-100 rounded"
              style={{
                zIndex: 2,
                aspectRatio: 9 / 16,
                backgroundColor: 'white',
                maxHeight:
                  previewMode == PREVIEW_TYPE_COMPACT
                    ? COMPACT_MODE_HEIGHT
                    : '100%'
              }}
            >
              <OnImagesLoaded
                onLoaded={() => {
                  setIsImageLoaded(true);
                }}
                placeholder={<PreviewImagesSkeleton />}
              >
                <div className="position-relative d-flex justify-content-center align-items-center w-100 h-100">
                  <Carousel
                    interval={null}
                    activeIndex={index}
                    onSelect={handleSlideChange}
                    controls={true}
                    indicators={false}
                    touch={true}
                    className="w-100 h-100"
                  >
                    {mediaFiles.map((media, idx) => (
                      <Carousel.Item key={idx}>
                        {renderMedia(media, idx)}
                      </Carousel.Item>
                    ))}
                  </Carousel>
                </div>
              </OnImagesLoaded>
            </div>
          )}

        {(!mediaFiles || mediaFiles.length == 0) && (
          <div
            className="asset_wrapper w-100 h-100 rounded bg-gray"
            style={{
              zIndex: 2,
              aspectRatio: 9 / 16,
              maxHeight:
                previewMode == PREVIEW_TYPE_COMPACT
                  ? COMPACT_MODE_HEIGHT
                  : '100%'
            }}
          >
            <div className="d-flex w-100 justify-content-center h-100 align-items-center text-white">
              {t('lomavisCreator.preview.noMedia')}
            </div>
          </div>
        )}

        {mediaFiles && !created_in_lomavis && mediaFiles.length > 0 && (
          <div
            className="asset_wrapper w-100 h-100 rounded bg-gray"
            style={{
              zIndex: 2,
              aspectRatio: 9 / 16,
              maxHeight:
                previewMode == PREVIEW_TYPE_COMPACT
                  ? COMPACT_MODE_HEIGHT
                  : '100%'
            }}
          >
            <div
              className={'w-100 rounded'}
              style={{
                aspectRatio: 9 / 16,
                maxHeight:
                  previewMode == PREVIEW_TYPE_COMPACT
                    ? COMPACT_MODE_HEIGHT
                    : '100%',
                backgroundImage: `url(${mediaFiles[index]?.thumbnail_url})`
              }}
            >
              <OnImagesLoaded
                onLoaded={() => {
                  setIsImageLoaded(true);
                }}
                placeholder={<PreviewImagesSkeleton />}
              >
                <ImportedPostsMediaPreview
                  platform={platform}
                  mediaFiles={mediaFiles}
                />
              </OnImagesLoaded>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
